// fe/src/modules/billing/planApi.ts
// Client for /api/v1/plan plus shared helpers for the billing page, payment and upgrade popups.
// Also keeps the backend paid state in step with the add-user screen's localStorage paid flag.

import { useCallback, useEffect, useState } from "react";
import { API_V1_BASE_URL } from "../../config/api";

// ---- API contract ---------------------------------------------------------

export interface PlanLineItem {
    code: string;
    description: string;
    quantity: number;
    unitPrice: number;
    amount: number;
}

export interface PlanQuote {
    currency: string;
    lineItems: PlanLineItem[];
    subtotal: number;
    taxRate: number;
    tax: number;
    total: number;
}

export interface PlanInvoice {
    id: string;
    invoiceNumber: string;
    status: "PAID";
    issuedAt: string;
    paidAt: string;
    periodStart: string;
    periodEnd: string;
    userLimit: number;
    storageLimitGb: number;
    storageLimitLabel?: string;
    currency: string;
    lineItems: PlanLineItem[];
    subtotal: number;
    tax: number;
    total: number;
    paymentMethod: { brand: string; last4: string; holderName: string };
    paidBy: string;
}

export interface PendingUpgrade {
    ticketId: string;
    ticketReference: string;
    userLimit: number;
    storageLimitGb: number;
    requestedAt: string;
    requestedBy: string;
}

export interface PlanUsage {
    users: { used: number; limit: number; pct: number };
    storage: { usedBytes: number; limitBytes: number; usedGb: number; limitGb: number; pct: number };
}

export interface PlanResponse {
    tenant: { id: string; name: string };
    plan: {
        name: string;
        userLimit: number;
        storageLimitGb: number;
        storageLimitLabel: string;
        storageLimitValue: number;
        storageLimitUnit: string;
        editable: false;
        billingCycle: "monthly";
        price: PlanQuote;
    };
    pricing: { currency: string; platformFee: number; perUser: number; perGb: number; taxRate: number };
    payment: {
        status: "PAID" | "UNPAID";
        paidAt: string | null;
        periodStart: string | null;
        periodEnd: string | null;
        nextBillingDate: string | null;
    };
    usage: PlanUsage;
    invoices: PlanInvoice[];
    pendingUpgrade: PendingUpgrade | null;
}

// ---- requests ---------------------------------------------------------------

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const token = localStorage.getItem("accessToken");
    const res = await fetch(`${API_V1_BASE_URL}/plan${path}`, {
        ...init,
        headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(init.headers || {}),
        },
    });
    const body = await res.json().catch(() => null);
    if (!res.ok || !body?.success) {
        throw new Error(body?.message || `Request failed (${res.status})`);
    }
    return body.data as T;
}

export const getPlan = () => request<PlanResponse>("");

export const payPlan = (card: { last4?: string; holderName?: string } = {}) =>
    request<{ alreadyPaid: boolean; invoice: PlanInvoice | null; plan: PlanResponse }>("/pay", {
        method: "POST",
        body: JSON.stringify(card),
    });

export const requestPlanUpgrade = (input: { userLimit: number; storageLimitGb: number; note?: string; context?: string }) =>
    request<{ ticket: { id: string; reference: string }; plan: PlanResponse }>("/upgrade-request", {
        method: "POST",
        body: JSON.stringify(input),
    });

export const resetDemoPayment = async () => {
    const plan = await request<PlanResponse>("/demo/reset", { method: "POST" });
    clearUserSidePaid(plan.tenant.id);
    return plan;
};

// ---- bridge to the add-user screen ---------------------------------------------
// CustomerUserManagement stores its paid flag in localStorage under this key.
// Keep the format identical to its `planKey`.

const USER_SIDE_PREFIX = "planPaid:";

export const userSidePaidKey = (plan: PlanResponse) =>
    `${USER_SIDE_PREFIX}${plan.tenant.id}:${plan.plan.userLimit}:${plan.plan.storageLimitValue}${plan.plan.storageLimitUnit}`;

export const markUserSidePaid = (plan: PlanResponse) => {
    try {
        localStorage.setItem(userSidePaidKey(plan), "true");
    } catch {
        // Storage unavailable; the add-user screen will ask again.
    }
};

const clearUserSidePaid = (tenantId: string) => {
    try {
        Object.keys(localStorage)
            .filter((k) => k.startsWith(`${USER_SIDE_PREFIX}${tenantId}:`))
            .forEach((k) => localStorage.removeItem(k));
    } catch {
        // Nothing to clear.
    }
};

/** Paid on the add-user screen -> record it on the backend; paid on the backend -> set the add-user flag. */
async function syncUserSidePayment(plan: PlanResponse): Promise<PlanResponse> {
    if (plan.payment.status === "PAID") {
        markUserSidePaid(plan);
        return plan;
    }
    let paidThere = false;
    try {
        paidThere = localStorage.getItem(userSidePaidKey(plan)) === "true";
    } catch {
        paidThere = false;
    }
    if (!paidThere) return plan;
    const res = await payPlan({ holderName: "Paid while adding a user" });
    return res.plan;
}

export function usePlan() {
    const [plan, setPlan] = useState<PlanResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const refresh = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setPlan(await syncUserSidePayment(await getPlan()));
        } catch (e) {
            setError(e instanceof Error ? e.message : "Could not load your plan");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    return { plan, setPlan, loading, error, refresh };
}

// ---- demo scenarios ---------------------------------------------------------
// Preview-only: they replace the usage numbers on screen. Payment status stays real.
// The choice is kept in sessionStorage so the ingestion page shows the same scenario.

export type ScenarioId = "live" | "healthy" | "storage-warning" | "users-full" | "storage-full" | "both-full";

export const SCENARIOS: { id: ScenarioId; label: string }[] = [
    { id: "live", label: "Live data" },
    { id: "healthy", label: "Healthy (56% storage)" },
    { id: "storage-warning", label: "Storage at 92%" },
    { id: "users-full", label: "User limit reached" },
    { id: "storage-full", label: "Storage full" },
    { id: "both-full", label: "Both limits reached" },
];

const SCENARIO_KEY = "simb.planScenario";

export const getActiveScenario = (): ScenarioId => {
    try {
        const v = sessionStorage.getItem(SCENARIO_KEY) as ScenarioId | null;
        return v && SCENARIOS.some((s) => s.id === v) ? v : "live";
    } catch {
        return "live";
    }
};

export const setActiveScenario = (id: ScenarioId) => {
    try {
        if (id === "live") sessionStorage.removeItem(SCENARIO_KEY);
        else sessionStorage.setItem(SCENARIO_KEY, id);
    } catch {
        // Preview just won't carry across pages.
    }
};

export const scenarioLabel = (id: ScenarioId) => SCENARIOS.find((s) => s.id === id)?.label ?? "Live data";

export function applyScenario(usage: PlanUsage, id: ScenarioId): PlanUsage {
    if (id === "live") return usage;

    const userLimit = usage.users.limit;
    const limitBytes = usage.storage.limitBytes;
    const someUsers = userLimit > 1 ? Math.max(1, Math.min(userLimit - 1, Math.round(userLimit * 0.6))) : 1;

    const storagePct = { healthy: 56, "storage-warning": 92, "users-full": 56, "storage-full": 100, "both-full": 100 }[id];
    const users = id === "users-full" || id === "both-full" ? userLimit : someUsers;
    const usedBytes = Math.round((limitBytes * storagePct) / 100);

    return {
        users: { used: users, limit: userLimit, pct: pctOf(users, userLimit) },
        storage: {
            ...usage.storage,
            usedBytes,
            usedGb: Math.round((usedBytes / 1024 ** 3) * 100) / 100,
            pct: storagePct,
        },
    };
}

// ---- thresholds and formatting ------------------------------------------------

export const WARN_PCT = 90;

export type UsageLevel = "ok" | "warning" | "full";

export const pctOf = (used: number, limit: number) => (limit > 0 ? Math.round((used / limit) * 1000) / 10 : 0);

export const usageLevel = (used: number, limit: number): UsageLevel => {
    if (limit > 0 && used >= limit) return "full";
    if (pctOf(used, limit) >= WARN_PCT) return "warning";
    return "ok";
};

export const formatBytes = (bytes: number) => {
    if (bytes < 1024 ** 2) return `${Math.round(bytes / 1024)} KB`;
    if (bytes < 1024 ** 3) return `${Math.round(bytes / 1024 ** 2)} MB`;
    const gb = bytes / 1024 ** 3;
    return `${Number.isInteger(gb) ? gb : gb.toFixed(2)} GB`;
};

export const formatMoney = (amount: number, currency = "USD") =>
    new Intl.NumberFormat(undefined, { style: "currency", currency }).format(amount);

export const formatDate = (iso: string | null | undefined) =>
    iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "—";

/** Same formula as the backend; used for the live estimate in the upgrade popup. */
export function estimateMonthly(pricing: PlanResponse["pricing"], userLimit: number, storageLimitGb: number) {
    const subtotal = pricing.platformFee + userLimit * pricing.perUser + storageLimitGb * pricing.perGb;
    return Math.round(subtotal * (1 + pricing.taxRate) * 100) / 100;
}

/** Opens a printable invoice; the browser's print dialog saves it as PDF. */
export function openInvoicePrintView(invoice: PlanInvoice, tenantName: string) {
    const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]!));
    const m = (n: number) => formatMoney(n, invoice.currency);
    const rows = invoice.lineItems
        .map((li) => `<tr><td>${esc(li.description)}</td><td class="r">${m(li.amount)}</td></tr>`)
        .join("");

    const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(invoice.invoiceNumber)}</title>
<style>
body{font-family:system-ui,sans-serif;color:#1f2937;max-width:720px;margin:40px auto;padding:0 24px}
h1{font-size:22px;margin:0}table{width:100%;border-collapse:collapse;margin-top:24px}
td{padding:10px 0;border-bottom:1px solid #e5e7eb}.r{text-align:right}.muted{color:#6b7280;font-size:14px}
.tot td{font-weight:600;border-bottom:none}.paid{display:inline-block;padding:2px 10px;border-radius:999px;background:#dcfce7;color:#166534;font-size:13px}
.head{display:flex;justify-content:space-between;align-items:flex-start}
</style></head><body>
<div class="head"><div><h1>Invoice ${esc(invoice.invoiceNumber)}</h1>
<p class="muted">Billed to ${esc(tenantName)}<br>Issued ${formatDate(invoice.issuedAt)}<br>
Service period ${formatDate(invoice.periodStart)} to ${formatDate(invoice.periodEnd)}</p></div>
<div><strong>SiMB</strong><p class="paid">Paid</p></div></div>
<p class="muted">Plan: ${invoice.userLimit} users, ${esc(invoice.storageLimitLabel || `${invoice.storageLimitGb} GB`)} storage, billed monthly</p>
<table>${rows}
<tr><td class="muted">Subtotal</td><td class="r">${m(invoice.subtotal)}</td></tr>
${invoice.tax > 0 ? `<tr><td class="muted">Tax</td><td class="r">${m(invoice.tax)}</td></tr>` : ""}
<tr class="tot"><td>Total paid</td><td class="r">${m(invoice.total)}</td></tr></table>
<p class="muted">Paid ${formatDate(invoice.paidAt)} with ${esc(invoice.paymentMethod.brand)} ending ${esc(invoice.paymentMethod.last4)}.
This is a demo invoice.</p>
<script>window.onload=function(){window.print()}</script></body></html>`;

    const win = window.open("", "_blank");
    if (!win) return false;
    win.document.write(html);
    win.document.close();
    return true;
}